<template>
  <v-card>
    <v-stepper v-model="currentWorkflowStep" :alt-labels="true">
      <v-stepper-header>
        <v-toolbar flat>
          <v-toolbar-title class="text-h5 font-weight-regular">Create a new CSV Datasource</v-toolbar-title>
        </v-toolbar>
        <v-spacer></v-spacer>
        <template v-for="(step, idx) in steps">
          <v-stepper-step
            :key="`${idx + 1}-step`"
            :complete="currentWorkflowStep > idx + 1"
            :step="idx + 1"
          >
            {{ step.label }}
          </v-stepper-step>

          <v-divider
            v-if="idx + 1 !== steps.length"
            :key="idx + 1"
          ></v-divider>
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="(step, idx) in steps"
          :key="`${idx + 1}-content`"
          :step="idx + 1"
        >

          <component
            :is="step.component"
            @step-validation="setStepState"
          >
          </component>

          <v-card flat class="mt-12">
            <v-card-actions>
              <v-btn @click="nextStep(idx - 1)" :disabled="currentWorkflowStep === 1">
                <v-icon class="pr-1">mdi-arrow-left-bold-circle</v-icon>
                Previous
              </v-btn>
              <v-btn text @click.prevent="cancelWorkflow">Cancel</v-btn>
              <v-spacer></v-spacer>
              <v-btn
                v-if="!isLastStep"
                color="primary"
                @click="nextStep(idx + 1)"
                :disabled="!canContinue"
              >
                Next
                <v-icon class="pl-1">mdi-arrow-right-bold-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="isLastStep"
                color="primary"
                @click="completeWorkflow"
                :disabled="!canContinue"
              >
                Finish
                <v-icon class="pl-1">mdi-cloud-upload</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <div class="text-center">
      <v-overlay :value="showUploadProgress">
        <p>Uploading CSV for Processing</p>
        <v-progress-circular
          v-if="showUploadProgress"
          :rotate="-90"
          :size=100
          :width="15"
          :value="uploadProgress.progress"
        >
          {{ uploadProgress.progress }}%
        </v-progress-circular>
      </v-overlay>
    </div>
  </v-card>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'
import {
  SaveCsv,
  SelectCsvTransforms,
  UploadCsv,
  MatchCustomerByVinAndOther,
  MatchVinToLastCustomer,
  MatchAddressToCustomer,
  FixedCostRoi,
  CustomerRoiData,
  LastServiceSpend
} from './steps'

const { v4: uuid } = require('uuid')

// uploaded filename is the username + uuid
const generateFilename = function (username) {
  return `${username}/${uuid()}.csv`
}

export default {
  name: 'csv-datasource',
  components: {
    [SaveCsv.name]: SaveCsv,
    [SelectCsvTransforms.name]: SelectCsvTransforms,
    [UploadCsv.name]: UploadCsv,
    [MatchCustomerByVinAndOther.name]: MatchCustomerByVinAndOther,
    [MatchVinToLastCustomer.name]: MatchVinToLastCustomer,
    [MatchAddressToCustomer.name]: MatchAddressToCustomer,
    [FixedCostRoi.name]: FixedCostRoi,
    [CustomerRoiData.name]: CustomerRoiData,
    [LastServiceSpend.name]: LastServiceSpend
  },
  props: {
    dealerId: {
      required: true,
      type: [String, Number]
    }
  },
  data: function () {
    return {
      currentWorkflowStep: 1,
      stepStates: {},
      showUploadProgress: false
    }
  },

  computed: {
    ...mapState('CsvMappingStore', { availableSteps: 'workflowSteps', optionalSteps: 'selectedOptionalSteps', csvFile: 'csvFile' }),
    ...mapState('S3FileStore', { uploadProgress: 'currentUploadStats' }),
    steps () {
      return this.availableSteps.filter((step) => step.required || this.optionalSteps.includes(step.name))
    },
    canContinue () {
      return (this.stepStates[this.steps[this.currentWorkflowStep - 1].component])
    },
    isLastStep () {
      return this.currentWorkflowStep === this.steps.length
    }
  },
  watch: {
    dealerId: {
      immediate: true,
      handler (newVal) {
        this.updateState({ dealer_id: newVal })
      }
    }
  },
  methods: {
    ...mapMutations('CsvMappingStore', ['resetMapping', 'updateState']),
    ...mapGetters('CsvMappingStore', ['getDatasourcePayload']),

    /**
     *  WORKFLOW HELPERS
     */
    nextStep (currentStepIdx) {
      if (currentStepIdx === this.steps.length) {
        this.currentWorkflowStep = 1
      } else {
        this.currentWorkflowStep = currentStepIdx + 1
        this.$emit('validate-step', { stepName: this.steps[this.currentWorkflowStep - 1] })
      }
    },
    setStepState (payload) {
      this.stepStates = { ...this.stepStates, ...{ [payload.name]: payload.value } }
    },

    /**
     * COMPLETE WORKFLOW BY UPLOADING & SAVING or CANCEL
     */
    cancelWorkflow () {
      this.$router.push({ name: 'csv_datasources_path' })
    },
    async completeWorkflow () {
      try {
        this.showUploadProgress = true

        await this.$store.dispatch('getCurrentUser') // refresh tokens for s3 upload...
        const credentials = await this.$store.dispatch('getUserCredentials')
        // lies, lies & damn lies - docs & examples say cognito:sub == username
        // but it only works if we pass in credentials.identityId because
        // api calls underneath move from cognito to cognito federated identities
        // backed by the cognito user pool
        const uploadFilename = generateFilename(credentials.identityId)
        const s3UploadData = await this.$store.dispatch('S3FileStore/upload', {
          file: this.csvFile,
          bucket: process.env.VUE_APP_USER_UPLOAD_BUCKET,
          key: uploadFilename,
          credentials: credentials
        })
        this.$logger.trace(s3UploadData)
        // SETUP for creating the datasource...
        const datasourcePayload = this.getDatasourcePayload()(s3UploadData.Bucket, s3UploadData.Key, s3UploadData.Location)

        await this.$store.dispatch('DatasourceStore/createCsvDatasource', datasourcePayload)

        this.showUploadProgress = false
        this.$router.push({ name: 'csv_datasources_path' })
        this.$flash({
          showClose: true,
          message: 'Created Datasource ' + this.name,
          type: 'success',
          duration: 3000
        })
      } catch (error) {
        this.showUploadProgress = false
        this.$logger.trace(error)
        this.$flash({
          showClose: true,
          message: 'Error Creating CSV Datasource. ' + error.message,
          type: 'error',
          duration: 0
        })
      }
    }
  },
  mounted () {
    this.resetMapping()
  }
}
</script>
